import React, { useEffect, useRef, useState } from 'react';

import AdminPageHeader from '../components/common/AdminPageHeader';
import DirectoryResultRow from '../components/patientcard/DirectoryResultRow';
import { getPatientDirectory } from '../services/api/patientCardApi';
import {
  DIRECTORY_MODE,
  DIRECTORY_QUERY_MIN_LENGTH,
  DIRECTORY_LIMIT_DEFAULT,
  SECTION_REASON_CODE,
} from '../constants/patientCard';

// Typing pauses this long before a search fires. Debounced so rapid typing
// cannot fire one request per keystroke (the known request-storm class) —
// combined with the hard min-length gate below, nothing is ever requested
// for a query under DIRECTORY_QUERY_MIN_LENGTH characters.
const DEBOUNCE_MS = 350;

const MODE_OPTIONS = [
  { value: DIRECTORY_MODE.AUTO, label: 'Auto' },
  { value: DIRECTORY_MODE.NAME, label: 'Name' },
  { value: DIRECTORY_MODE.MOBILE, label: 'Mobile' },
  { value: DIRECTORY_MODE.UHID, label: 'UHID' },
];

const MODE_HINT_LABEL = {
  [DIRECTORY_MODE.MOBILE]: 'mobile number',
  [DIRECTORY_MODE.UHID]: 'UHID',
  [DIRECTORY_MODE.NAME]: 'name',
};

/**
 * resolveAutoModeHint — mirrors the FROZEN §1.3 AUTO lane-resolution rule
 * verbatim (10 digits ⇒ MOBILE · starts "UHID/" case-insensitive or exactly
 * 6 digits ⇒ UHID · else NAME), used ONLY to render a "will search by …"
 * hint before the server has answered. Display-only: the request always
 * carries the chip's own `mode` (AUTO by default), and once a response
 * comes back the server's `resolved_mode` is the single source of truth —
 * this hint never substitutes for it.
 */
export function resolveAutoModeHint(trimmed) {
  if (!trimmed || trimmed.length < DIRECTORY_QUERY_MIN_LENGTH) return null;
  const compact = trimmed.replace(/[\s-]/g, '');
  if (/^\d{10}$/.test(compact)) return DIRECTORY_MODE.MOBILE;
  if (/^uhid\//i.test(trimmed) || /^\d{6}$/.test(trimmed)) return DIRECTORY_MODE.UHID;
  return DIRECTORY_MODE.NAME;
}

const EMPTY_ATTEMPT = { q: '', mode: DIRECTORY_MODE.AUTO };

/**
 * PatientDirectoryPage (EPIC PATIENT-CARD, F4) — `/org/patients`, admin-only.
 * Binds §1.3 `GET /api/organisation/patients/directory` verbatim via
 * `getPatientDirectory` (`services/api/patientCardApi.js`) — no second
 * client, no re-declared vocab (all modes/limits from
 * `constants/patientCard.js`).
 *
 * Zero queries fire on mount or below `DIRECTORY_QUERY_MIN_LENGTH` (3)
 * characters — no `useEffect` ever triggers a fetch here, so a re-render can
 * never re-fire one; every request is the direct result of a user action
 * (typing past the debounce, Search/Enter, a mode chip, or Load more/Retry).
 *
 * Route wiring (`ROUTES.PATIENT_DIRECTORY`, `App.js`, the dashboard tile) is
 * F9's task, not this one — this page is not mounted into the app router
 * yet.
 */
export default function PatientDirectoryPage() {
  const [text, setText] = useState('');
  const [mode, setMode] = useState(DIRECTORY_MODE.AUTO);
  const [everSearched, setEverSearched] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | loaded | error
  const [results, setResults] = useState([]);
  const [resolvedMode, setResolvedMode] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [reasonCode, setReasonCode] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [attempted, setAttempted] = useState(EMPTY_ATTEMPT);

  const debounceRef = useRef(null);
  // Bumped on every new search; a response is applied only if it's still the
  // latest one requested — guards against an old mode/query's response
  // landing after a newer search has already started.
  const requestIdRef = useRef(0);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const trimmed = text.trim();
  const belowMin = trimmed.length < DIRECTORY_QUERY_MIN_LENGTH;
  const hint = mode === DIRECTORY_MODE.AUTO ? resolveAutoModeHint(trimmed) : null;

  const runSearch = (q, searchMode) => {
    const myId = ++requestIdRef.current;
    setEverSearched(true);
    setStatus('loading');
    setError(null);
    setAttempted({ q, mode: searchMode });
    getPatientDirectory({ q, mode: searchMode, limit: DIRECTORY_LIMIT_DEFAULT }).then((res) => {
      if (myId !== requestIdRef.current) return; // superseded by a newer search
      if (res.success) {
        const data = res.data || {};
        // LANE_READ_FAILED is carried on an otherwise-200 response — the read
        // genuinely failed, so it renders the SAME error+retry state as a
        // transport failure, not the "not available yet" info state.
        if (data.reason_code === SECTION_REASON_CODE.LANE_READ_FAILED) {
          setResults([]);
          setResolvedMode(null);
          setNextCursor(null);
          setReasonCode(null);
          setError('Could not search patients.');
          setStatus('error');
          return;
        }
        setResults(Array.isArray(data.patients) ? data.patients : []);
        setResolvedMode(data.resolved_mode || null);
        setNextCursor(data.next_cursor || null);
        setTruncated(!!data.truncated);
        setReasonCode(data.reason_code || null);
        setStatus('loaded');
      } else {
        setResults([]);
        setResolvedMode(null);
        setNextCursor(null);
        setReasonCode(null);
        setError(res.error || 'Could not search patients.');
        setStatus('error');
      }
    });
  };

  const triggerSearch = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const q = text.trim();
    if (q.length < DIRECTORY_QUERY_MIN_LENGTH) return;
    runSearch(q, mode);
  };

  const onInput = (e) => {
    const next = e.target.value;
    setText(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = next.trim();
    if (q.length < DIRECTORY_QUERY_MIN_LENGTH) return; // below the min ⇒ never scheduled, never fired
    debounceRef.current = setTimeout(() => runSearch(q, mode), DEBOUNCE_MS);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    triggerSearch();
  };

  const onModeChange = (nextMode) => {
    if (nextMode === mode) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    requestIdRef.current += 1; // any in-flight response for the old mode is now stale
    setMode(nextMode);
    setStatus('idle');
    setResults([]);
    setResolvedMode(null);
    setNextCursor(null);
    setTruncated(false);
    setReasonCode(null);
    setError(null);
    setAttempted(EMPTY_ATTEMPT);
  };

  const onRetry = () => {
    if (!attempted.q) return;
    runSearch(attempted.q, attempted.mode);
  };

  const onLoadMore = () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    getPatientDirectory({
      q: attempted.q,
      mode: attempted.mode,
      limit: DIRECTORY_LIMIT_DEFAULT,
      cursor: nextCursor,
    }).then((res) => {
      setLoadingMore(false);
      if (res.success) {
        const data = res.data || {};
        const more = Array.isArray(data.patients) ? data.patients : [];
        setResults((cur) => [...cur, ...more]);
        setNextCursor(data.next_cursor || null);
        setTruncated(!!data.truncated);
      } else {
        setError(res.error || 'Could not load more patients.');
      }
    });
  };

  const searchDisabled = belowMin;
  const showHero = !everSearched;
  const resolvedModeLabel = resolvedMode ? (MODE_HINT_LABEL[resolvedMode] || resolvedMode) : null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <AdminPageHeader />
      <div className="mx-auto max-w-3xl p-4" data-testid="patient-directory-page">
        <form onSubmit={onSubmit} className={showHero ? 'text-center py-10' : 'py-4'} data-testid="directory-hero">
          {showHero && (
            <>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">Find a patient</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                Search across every patient in this organisation — by name, mobile number, or UHID.
              </p>
            </>
          )}
          <div className={`flex gap-2 ${showHero ? 'max-w-md mx-auto' : ''}`}>
            <input
              type="text"
              value={text}
              onChange={onInput}
              placeholder="e.g. Rakesh Kumar · 9876543210 · UHID/000123"
              aria-label="Search patients"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="directory-search-input"
            />
            <button
              type="submit"
              disabled={searchDisabled}
              className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="directory-search-btn"
            >
              Search
            </button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 justify-center">
            {MODE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onModeChange(opt.value)}
                aria-pressed={mode === opt.value}
                className={`px-2.5 py-1 rounded-full text-[11.5px] font-semibold border ${
                  mode === opt.value
                    ? 'bg-teal-600 border-teal-600 text-white'
                    : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200'
                }`}
                data-testid={`directory-mode-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-[12px] text-slate-500 dark:text-slate-400 text-center" data-testid="directory-search-reason">
            {searchDisabled
              ? `Type at least ${DIRECTORY_QUERY_MIN_LENGTH} characters to search.`
              : hint
                ? `Will search by ${MODE_HINT_LABEL[hint]}.`
                : ' '}
          </p>
        </form>

        {everSearched && (
          <div className="mt-2">
            {status === 'idle' && (
              <p className="text-center text-[12px] text-slate-400 dark:text-slate-500 mt-4" data-testid="directory-idle-after-mode-switch">
                Press Search to search by {MODE_OPTIONS.find((o) => o.value === mode)?.label.toLowerCase()}.
              </p>
            )}

            {status === 'loading' && (
              <div className="space-y-2" data-testid="directory-skeleton">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-14 rounded-lg bg-slate-200 dark:bg-slate-700 animate-pulse" />
                ))}
              </div>
            )}

            {status === 'error' && (
              <div
                className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm p-4 text-center"
                role="alert"
                data-testid="directory-error"
              >
                <p className="mb-2">{error}</p>
                <button
                  type="button"
                  onClick={onRetry}
                  className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-[12.5px] font-semibold"
                  data-testid="directory-retry"
                >
                  Retry
                </button>
              </div>
            )}

            {status === 'loaded' && results.length === 0 && reasonCode === SECTION_REASON_CODE.NOT_IMPLEMENTED && (
              <div className="rounded-lg border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 p-6 text-center" data-testid="directory-name-unavailable">
                <p className="font-semibold text-blue-900 dark:text-blue-100">
                  Name search isn&apos;t available yet
                </p>
                <p className="text-[12.5px] text-blue-800 dark:text-blue-200 mt-1">
                  This search did not run. The patient may still be in your organisation&apos;s records — search by mobile number or UHID to find them.
                </p>
              </div>
            )}

            {status === 'loaded' && results.length === 0 && reasonCode !== SECTION_REASON_CODE.NOT_IMPLEMENTED && (
              <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 p-6 text-center" data-testid="directory-zero">
                <p className="font-semibold text-slate-700 dark:text-slate-200">
                  {`No patients match "${attempted.q}"`}
                </p>
                <p className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-1">
                  {resolvedModeLabel
                    ? `Searched by ${resolvedModeLabel}. Check the spelling, try just the mobile digits, or the full UHID.`
                    : 'Check the spelling, try just the mobile digits, or the full UHID.'}
                </p>
              </div>
            )}

            {status === 'loaded' && results.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2 text-[12.5px] text-slate-500 dark:text-slate-400">
                  <span data-testid="directory-result-count">
                    {`${results.length} patient${results.length === 1 ? '' : 's'} found for "${attempted.q}"`}
                    {resolvedModeLabel ? ` · searched by ${resolvedModeLabel}` : ''}
                  </span>
                </div>
                <div className="flex flex-col gap-2" data-testid="directory-results">
                  {results.map((p) => (
                    <DirectoryResultRow key={p.patient_id} patient={p} />
                  ))}
                </div>
                {truncated && (
                  <p className="mt-2 text-[11.5px] text-slate-400 dark:text-slate-500 text-center">
                    Too many matches — refine the search to narrow it down.
                  </p>
                )}
                {nextCursor && (
                  <button
                    type="button"
                    onClick={onLoadMore}
                    disabled={loadingMore}
                    className="mt-3 w-full px-3 py-2 rounded-lg border border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 text-[12.5px] font-semibold disabled:opacity-60"
                    data-testid="directory-load-more"
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
